import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Archive, Pencil, Plus, RotateCcw } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useCurrency } from '@/Hooks/useCurrency';
import { useAppDispatch } from '@/Redux/store';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import {
  PRICING_LIST_PARAM,
  RESOURCES,
  pricingListPath,
  pricingListRulePath,
} from './pricingRoutes';
import {
  invalidatePricingList,
  resourceCountOf,
  ruleCountOf,
  usePricingListQuery,
  usePricingListsQuery,
  type PricingListRef,
  type PricingListRow,
  type PricingRuleRow,
} from './pricingListsApi';
import { refusalOf, type Refusal } from './lib/refusals';
import { useRefusalToast } from './lib/useRefusal';
import { draftOf } from './lib/rule-draft';
import { useRuleLabel } from './components/PricingRuleList';
import { PricingRuleFormDialog, WorkedExample } from './components/PricingRuleDialogs';
import { PricingListFormDialog } from './components/PricingListFormDialog';
import { RefusalNotice } from './components/RefusalNotice';

/**
 * # Rate cards, as a thing in their own right
 *
 * The editor that shipped attached rules to one resource at a time, and the
 * venue owner rejected it in one sentence: *"I need to set up price rules as a
 * price list on the system, then I can edit any resource and attach the price
 * list to it."* A venue with ten PlayStations should type its hourly rate once.
 *
 * So this screen is not "pricing for a resource" moved somewhere roomier. It is
 * the rate card as the object being edited, with the consoles listed underneath
 * as *consequences* — because the question a manager actually has when they put
 * the evening rate up is "what did I just change the price of", and a per-console
 * editor could never answer it.
 *
 * ## Why it lives under Settings and not under Catalog
 *
 * Catalog is gated on `menu.view` and is about things a guest orders off a menu:
 * items, meals, modifiers, packages, the stations that cook them. An hourly rate
 * for a PlayStation is none of those — nobody orders it, it has no station, and
 * it is priced by elapsed time rather than per line.
 *
 * Settings is where this app keeps the tenant-level registries every other
 * screen assumes already exist — floors and areas, branches, devices, the
 * guest-facing switches. A rate card is exactly that shape: created once, named,
 * and then *picked* from somewhere else. The controller agrees, in the way that
 * matters most: `@Controller('pricing-lists')` carries no `@RequireScope`,
 * because a card belongs to the tenant and has no place in the venue topology.
 * A screen under the branch-scoped half of the app would have implied otherwise.
 *
 * It sits immediately after Spaces because those two are what a resource needs
 * before it can trade: somewhere to stand, and a price.
 *
 * ## The selection is in the URL
 *
 * `?list=…`, like the staff id the Settings staff list hands to the assignments
 * tab. A refusal on the floor that says "add a rate to Standard rates" has to
 * land on Standard rates; a screen that opened on whichever card sorted first
 * would make the operator find it again, which is the same failure this change
 * is about one level down.
 */

/** A resource as this screen reads it: a name, and the card it charges from. */
interface AttachedResource {
  id: string;
  name: string;
  pricingList?: PricingListRef | null;
}

export default function PricingListsPanel() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { code: currency } = useCurrency();
  const dispatch = useAppDispatch();
  const askConfirm = useConfirm().confirm;
  const toastRefusal = useRefusalToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const canView = can('resources.view');
  const canCreate = can('resources.create');
  const canUpdate = can('resources.update');
  const canDeactivate = can('resources.delete');

  const listsQuery = usePricingListsQuery(undefined, { skip: !canView });
  const lists = useMemo(() => listsQuery.data ?? [], [listsQuery.data]);

  /**
   * The card the URL names, or the first one.
   *
   * Falling back rather than showing nothing: a manager who opened the tab from
   * the nav has named no card and still wants to be looking at one. A `?list=`
   * naming a card that no longer exists falls back the same way instead of
   * showing an empty panel that looks broken.
   */
  const requested = searchParams.get(PRICING_LIST_PARAM);
  const selectedId = lists.some((list) => list.id === requested)
    ? (requested as string)
    : (lists[0]?.id ?? null);

  const select = (listId: string | null) => {
    setSearchParams(
      (params) => {
        if (listId) params.set(PRICING_LIST_PARAM, listId);
        else params.delete(PRICING_LIST_PARAM);
        return params;
      },
      { replace: true },
    );
  };

  const detailQuery = usePricingListQuery(selectedId ?? '', { skip: !canView || !selectedId });
  const detail = detailQuery.data ?? null;

  /**
   * Which resources charge from which card.
   *
   * Read once for the whole tenant rather than per card, because the answer this
   * screen most needs to give is the one no single card can: *which consoles are
   * on nothing at all*. That set is a venue that cannot start a timer, and it is
   * invisible from inside any one rate card.
   *
   * 200 is the API's own page ceiling, and a venue with more timed resources than
   * that has bigger problems than this panel — the counts on each card come off
   * the index and stay right regardless.
   */
  const resourcesQuery = useApi<unknown>(canView ? RESOURCES : null, { limit: 200 });
  const resources = useMemo(
    () => rowsOf<AttachedResource>(resourcesQuery.data).rows,
    [resourcesQuery.data],
  );
  const attachedTo = useMemo(() => {
    const byList = new Map<string, AttachedResource[]>();
    for (const resource of resources) {
      const key = resource.pricingList?.id;
      if (!key) continue;
      const bucket = byList.get(key) ?? [];
      bucket.push(resource);
      byList.set(key, bucket);
    }
    return byList;
  }, [resources]);
  const unattached = useMemo(
    () => resources.filter((resource) => !resource.pricingList?.id),
    [resources],
  );

  const [editingList, setEditingList] = useState<{ open: boolean; record: PricingListRow | null }>({
    open: false,
    record: null,
  });
  const [ruleForm, setRuleForm] = useState<{ open: boolean; record: PricingRuleRow | null }>({
    open: false,
    record: null,
  });
  const [listRefusal, setListRefusal] = useState<Refusal | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = () => {
    dispatch(invalidatePricingList(selectedId ?? undefined));
    resourcesQuery.reload();
  };

  /**
   * Retiring a card, which the API refuses while a resource still charges from
   * it — and the refusal names the consoles to move.
   *
   * Kept on the panel rather than thrown at a toast because that list of names
   * IS the work: a notice that has faded cannot be read twice.
   */
  const retire = async (list: PricingListRow) => {
    const ok = await askConfirm({
      title: t('spaces.pricingRules.lists.retireTitle', { name: list.name }),
      body: t('spaces.pricingRules.lists.retireBody'),
      confirmLabel: t('spaces.pricingRules.lists.retire'),
      tone: 'danger' as const,
    });
    if (!ok) return;
    setBusyId(list.id);
    setListRefusal(null);
    try {
      await api.delete(pricingListPath(list.id));
      toast.success(t('spaces.pricingRules.lists.retired', { name: list.name }));
      refresh();
    } catch (error) {
      setListRefusal(refusalOf(error));
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (list: PricingListRow) => {
    setBusyId(list.id);
    setListRefusal(null);
    try {
      await api.patch(pricingListPath(list.id), { isActive: true });
      toast.success(t('spaces.pricingRules.lists.restored', { name: list.name }));
      refresh();
    } catch (error) {
      setListRefusal(refusalOf(error));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Deactivate a rate, never delete it — and say so, because "Delete" is what
   * the button looks like it does. `ResourceSession.pricingRuleId` points at
   * this row, so destroying it would cut every settled session loose from the
   * rate it was charged under, and a receipt reprinted in March would quote
   * March's price for a January session.
   */
  const deactivateRule = async (rule: PricingRuleRow) => {
    if (!selectedId) return;
    const ok = await askConfirm({
      title: t('spaces.pricingRules.deactivateTitle', { name: rule.name }),
      body: t('spaces.pricingRules.deactivateBody'),
      confirmLabel: t('spaces.pricingRules.deactivate'),
      tone: 'danger' as const,
    });
    if (!ok) return;
    setBusyId(rule.id);
    try {
      await api.delete(pricingListRulePath(selectedId, rule.id));
      toast.success(t('spaces.pricingRules.deactivated', { name: rule.name }));
      refresh();
    } catch (error) {
      toastRefusal(error, { subject: rule.name });
    } finally {
      setBusyId(null);
    }
  };

  /**
   * The other half of deactivation, and the reason retired rates stay listed:
   * `RESOURCE_PRICING_RULE_MISSING` says "every rate on this card is switched
   * off", and an editor that hid them would leave only "write another" open.
   */
  const reactivateRule = async (rule: PricingRuleRow) => {
    if (!selectedId) return;
    setBusyId(rule.id);
    try {
      await api.patch(pricingListRulePath(selectedId, rule.id), { isActive: true });
      toast.success(t('spaces.pricingRules.reactivated', { name: rule.name }));
      refresh();
    } catch (error) {
      // Reactivating can collide with a rate that took over the same window
      // while this one was off, so it gets the same 409 a save does.
      toastRefusal(error, { subject: rule.name });
    } finally {
      setBusyId(null);
    }
  };

  if (!canView) {
    return (
      <EmptyState
        title={t('spaces.pricingRules.noAccess')}
        hint={t('spaces.pricingRules.noAccessHint')}
      />
    );
  }

  if (listsQuery.isLoading) return <LoadingState label={t('common.loading')} />;
  if (listsQuery.error) {
    return (
      <ErrorState
        message={t('spaces.pricingRules.loadFailed')}
        onRetry={() => void listsQuery.refetch()}
      />
    );
  }

  const activeRules = detail?.rules.filter((rule) => rule.isActive) ?? [];
  const retiredRules = detail?.rules.filter((rule) => !rule.isActive) ?? [];
  const usedBy = selectedId ? (attachedTo.get(selectedId) ?? []) : [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t('spaces.pricingRules.lists.title')}</h2>
          <p className="text-sm text-ink-muted">{t('spaces.pricingRules.lists.subtitle')}</p>
        </div>
        {canCreate && (
          <Button onClick={() => setEditingList({ open: true, record: null })}>
            <Plus className="h-4 w-4" />
            {t('spaces.pricingRules.lists.create')}
          </Button>
        )}
      </div>

      {lists.length === 0 ? (
        <EmptyState
          title={t('spaces.pricingRules.lists.empty')}
          hint={t('spaces.pricingRules.lists.emptyHint')}
          action={
            canCreate ? (
              <Button className="mt-2" onClick={() => setEditingList({ open: true, record: null })}>
                <Plus className="h-4 w-4" />
                {t('spaces.pricingRules.lists.createFirst')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
          {/* ---- the cards ------------------------------------------------ */}
          <nav aria-label={t('spaces.pricingRules.lists.title')}>
            <ul className="space-y-2">
              {lists.map((list) => {
                const chosen = list.id === selectedId;
                return (
                  <li key={list.id}>
                    <button
                      type="button"
                      onClick={() => select(list.id)}
                      aria-current={chosen ? 'true' : undefined}
                      className={[
                        'w-full rounded-lg border p-3 text-start',
                        chosen ? 'border-accent-line bg-accent-soft' : 'hover:bg-hover',
                        list.isActive ? '' : 'border-dashed opacity-80',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                        <span dir="auto">{list.name}</span>
                        {!list.isActive && (
                          <Badge tone="neutral">
                            {t('spaces.pricingRules.lists.retiredBadge')}
                          </Badge>
                        )}
                      </span>
                      {/* The two counts are the reason the index carries them:
                          "0 resources" is a card nobody charges from, and
                          "0 rates" is a card that cannot price anything. */}
                      <span className="mt-1 block text-xs text-ink-muted">
                        {t('spaces.pricingRules.lists.counts', {
                          rules: ruleCountOf(list),
                          resources: resourceCountOf(list),
                        })}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* ---- the chosen card ------------------------------------------ */}
          <div className="min-w-0 space-y-4">
            {detailQuery.isLoading || !detail ? (
              <LoadingState label={t('common.loading')} />
            ) : (
              <>
                <Card>
                  <CardContent className="space-y-3 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-base font-semibold" dir="auto">
                          {detail.name}
                        </p>
                        {detail.description && (
                          <p className="text-sm text-ink-muted" dir="auto">
                            {detail.description}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        {canUpdate && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === detail.id}
                            onClick={() => setEditingList({ open: true, record: detail })}
                          >
                            <Pencil className="h-4 w-4" />
                            {t('spaces.pricingRules.lists.rename')}
                          </Button>
                        )}
                        {detail.isActive && canDeactivate && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === detail.id}
                            onClick={() => void retire(detail)}
                          >
                            <Archive className="h-4 w-4" />
                            {t('spaces.pricingRules.lists.retire')}
                          </Button>
                        )}
                        {!detail.isActive && canUpdate && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === detail.id}
                            onClick={() => void restore(detail)}
                          >
                            <RotateCcw className="h-4 w-4" />
                            {t('spaces.pricingRules.lists.restore')}
                          </Button>
                        )}
                      </div>
                    </div>

                    {listRefusal && (
                      <RefusalNotice refusal={listRefusal} tone="danger" />
                    )}

                    {/* What editing this card actually moves. A per-console
                        editor could never show this, which is why nobody could
                        tell what putting the evening rate up would change. */}
                    <div className="rounded-lg bg-[var(--muted)] p-3">
                      <p className="text-xs font-medium">
                        {t('spaces.pricingRules.lists.usedByHeading')}
                      </p>
                      {usedBy.length === 0 ? (
                        <p className="mt-1 text-xs text-ink-muted">
                          {t('spaces.pricingRules.lists.usedByNone')}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-ink-muted" dir="auto">
                          {t('spaces.pricingRules.lists.usedBy', {
                            count: usedBy.length,
                            names: usedBy.map((resource) => resource.name).join(', '),
                          })}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <section className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold">
                      {t('spaces.pricingRules.activeHeading')}
                    </h3>
                    {canCreate && (
                      <Button size="sm" onClick={() => setRuleForm({ open: true, record: null })}>
                        <Plus className="h-4 w-4" />
                        {t('spaces.pricingRules.add')}
                      </Button>
                    )}
                  </div>

                  {activeRules.length === 0 ? (
                    // The gap the whole feature exists to close: a card with no
                    // rate cannot start a timer on any resource attached to it,
                    // so the empty state carries the way out rather than merely
                    // reporting the absence.
                    <EmptyState
                      title={t('spaces.pricingRules.empty')}
                      hint={t('spaces.pricingRules.emptyHint')}
                      action={
                        canCreate ? (
                          <Button
                            className="mt-2"
                            onClick={() => setRuleForm({ open: true, record: null })}
                          >
                            <Plus className="h-4 w-4" />
                            {t('spaces.pricingRules.addFirst')}
                          </Button>
                        ) : undefined
                      }
                    />
                  ) : (
                    <ul className="space-y-2">
                      {activeRules.map((rule) => (
                        <RuleRow
                          key={rule.id}
                          rule={rule}
                          currency={currency}
                          busy={busyId === rule.id}
                          onEdit={
                            canUpdate ? () => setRuleForm({ open: true, record: rule }) : undefined
                          }
                          onDeactivate={
                            canDeactivate ? () => void deactivateRule(rule) : undefined
                          }
                        />
                      ))}
                    </ul>
                  )}
                </section>

                {retiredRules.length > 0 && (
                  <section className="space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('spaces.pricingRules.inactiveHeading')}
                    </h3>
                    <p className="text-xs text-ink-muted">
                      {t('spaces.pricingRules.inactiveHint')}
                    </p>
                    <ul className="space-y-2">
                      {retiredRules.map((rule) => (
                        <RuleRow
                          key={rule.id}
                          rule={rule}
                          currency={currency}
                          busy={busyId === rule.id}
                          onReactivate={
                            canUpdate ? () => void reactivateRule(rule) : undefined
                          }
                        />
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* A console on no card at all cannot start a timer, and that fact is
          invisible from inside any single rate card — which is exactly why it is
          reported here, once, for the whole venue. */}
      {unattached.length > 0 && (
        <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          <p className="font-medium">{t('spaces.pricingRules.lists.unattachedTitle')}</p>
          <p dir="auto">
            {t('spaces.pricingRules.lists.unattachedBody', {
              count: unattached.length,
              names: unattached.map((resource) => resource.name).join(', '),
            })}
          </p>
        </div>
      )}

      <PricingListFormDialog
        open={editingList.open}
        record={editingList.record}
        onClose={() => setEditingList({ open: false, record: null })}
        onSaved={(list) => {
          refresh();
          select(list.id);
        }}
      />

      <PricingRuleFormDialog
        open={ruleForm.open}
        listId={selectedId}
        record={ruleForm.record}
        currency={currency}
        activeRuleCount={activeRules.length}
        onClose={() => setRuleForm({ open: false, record: null })}
        onSaved={refresh}
      />
    </div>
  );
}

/**
 * One rate, with what it charges spelled out underneath.
 *
 * The worked example is on the row and not only in the form on purpose: the
 * rounding is what surprises people — every unit rounds *up*, so an hourly rate
 * bills two hours for a 61-minute visit — and a manager reviewing a card they
 * wrote last month is exactly who needs reminding before a guest does.
 */
function RuleRow({
  rule,
  currency,
  busy,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  rule: PricingRuleRow;
  currency: string;
  busy: boolean;
  onEdit?: () => void;
  onDeactivate?: () => void;
  onReactivate?: () => void;
}) {
  const { t } = useTranslation();
  const label = useRuleLabel(currency);

  return (
    <li
      className={
        rule.isActive
          ? 'space-y-2 rounded-lg border p-3'
          : 'space-y-2 rounded-lg border border-dashed bg-[var(--muted)] p-3 opacity-80'
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
            <span dir="auto">{rule.name}</span>
            {!rule.isActive && (
              <Badge tone="neutral">{t('spaces.pricingRules.inactiveBadge')}</Badge>
            )}
            {rule.priority !== 0 && (
              <Badge tone="info">
                {t('spaces.pricingRules.priorityBadge', { count: rule.priority })}
              </Badge>
            )}
          </p>
          {/* The rate, the window and the minimum, as one sentence. Forced LTR
              because it is a run of digits and separators that Arabic would
              otherwise reorder into a different time range. */}
          <p className="text-xs text-ink-muted" dir="ltr">
            {label(rule)}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          {onEdit && (
            <Button size="sm" variant="outline" disabled={busy} onClick={onEdit}>
              <Pencil className="h-4 w-4" />
              {t('common.edit')}
            </Button>
          )}
          {onDeactivate && (
            <Button size="sm" variant="outline" disabled={busy} onClick={onDeactivate}>
              <Archive className="h-4 w-4" />
              {t('spaces.pricingRules.deactivate')}
            </Button>
          )}
          {onReactivate && (
            <Button size="sm" variant="outline" disabled={busy} onClick={onReactivate}>
              <RotateCcw className="h-4 w-4" />
              {t('spaces.pricingRules.reactivate')}
            </Button>
          )}
        </div>
      </div>

      <WorkedExample draft={draftOf(rule)} currency={currency} />
    </li>
  );
}
